import { motion } from "framer-motion";
import { Banknote, Clock, Crown, LineChart, Smartphone, Users, Zap, ArrowRight, Infinity as InfinityIcon } from "lucide-react";
import { ChessQueen } from "@/components/ui/ChessQueen";
import { Link } from "react-router-dom";

const benefits = [
  {
    icon: Banknote,
    title: "Comissões Atrativas",
    description: "Ganhe até 3% de comissão única por contrato fechado, paga direto na sua conta.",
  },
  {
    icon: Zap,
    title: "Aprovação Rápida",
    description: "Cadastro simplificado e aprovação em até 24 horas. Comece a indicar clientes imediatamente.",
  },
  {
    icon: Smartphone,
    title: "Plataforma Intuitiva",
    description: "Painel exclusivo para acompanhar suas indicações, contratos e comissões em tempo real.",
  },
  {
    icon: Users,
    title: "Suporte Dedicado",
    description: "Time especializado para ajudar você e seus clientes em todas as etapas do processo.",
  },
  {
    icon: LineChart,
    title: "Relatórios Completos",
    description: "Dashboards e relatórios detalhados para você acompanhar seu desempenho e metas.",
  },
  {
    icon: Clock,
    title: "Pagamento Pontual",
    description: "Comissão paga em até 30 dias após o fechamento do contrato. Transparência total.",
  },
];

const Benefits = () => {
  return (
    <section id="beneficios" className="py-20 bg-background">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-14"
        >
          <h2 className="text-3xl md:text-4xl font-extrabold text-foreground mb-4 tracking-tight">
            Por que ser nosso parceiro?
          </h2>
          <p className="text-base text-muted-foreground max-w-xl mx-auto font-light">
            Oferecemos as melhores condições do mercado para você crescer conosco
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {benefits.map((benefit, index) => (
            <motion.div
              key={benefit.title}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: index * 0.06 }}
              className="group p-6 bg-card/50 rounded-xl border border-border/40 hover:border-primary/20 hover:bg-card/80 transition-all duration-300"
            >
              <div className="w-10 h-10 rounded-lg bg-primary/8 flex items-center justify-center mb-4 group-hover:bg-primary/12 transition-colors">
                <benefit.icon className="w-5 h-5 text-primary" />
              </div>
              <h3 className="text-base font-semibold text-foreground mb-1.5">
                {benefit.title}
              </h3>
              <p className="text-muted-foreground text-sm leading-relaxed font-light">
                {benefit.description}
              </p>
            </motion.div>
          ))}
        </div>

        {/* Ambassador Highlight Card — mesmo estilo do Acelera Imob */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="group relative mt-14 rounded-xl border border-transparent bg-gradient-to-br from-primary/15 via-card/80 to-accent/10 backdrop-blur-md ring-1 ring-primary/40 shadow-[0_0_40px_-10px_hsl(var(--primary)/0.4)] hover:shadow-[0_0_60px_-10px_hsl(var(--primary)/0.55)] transition-all duration-500 p-6 md:p-8"
        >
          {/* Glow decorativo */}
          <div aria-hidden className="pointer-events-none absolute inset-0 rounded-xl overflow-hidden">
            <div className="absolute -top-24 -right-24 w-56 h-56 rounded-full bg-primary/20 blur-3xl" />
            <div className="absolute -bottom-24 -left-24 w-56 h-56 rounded-full bg-accent/15 blur-3xl" />
          </div>

          {/* Badge topo */}
          <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10">
            <span className="bg-gradient-to-r from-primary to-accent text-primary-foreground text-[10px] font-bold px-3 py-1 rounded-full uppercase tracking-widest shadow-lg shadow-primary/30 flex items-center gap-1.5">
              <Crown className="w-3 h-3" />
              Programa Exclusivo
            </span>
          </div>

          <div className="relative grid md:grid-cols-[auto_1fr_auto] gap-6 md:gap-8 items-center">
            {/* Emblema */}
            <div className="flex md:block justify-center">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center bg-gradient-to-br from-primary to-accent text-primary-foreground shadow-lg shadow-primary/30">
                <Crown className="w-6 h-6" />
              </div>
            </div>

            {/* Content */}
            <div className="text-center md:text-left">
              <h3 className="text-xl md:text-2xl font-bold mb-2 tracking-tight bg-gradient-to-r from-foreground via-primary to-foreground bg-clip-text text-transparent">
                Torne-se um Embaixador Aceleraí
              </h3>
              <p className="text-muted-foreground text-sm font-light leading-relaxed max-w-2xl">
                Indique 3 parceiros e desbloqueie comissões recorrentes vitalícias da primeira camada.
                Você é dono da sua rede — para sempre.
              </p>

              <div className="flex flex-wrap gap-2 mt-4 justify-center md:justify-start">
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-background/60 border border-primary/20 text-xs text-foreground">
                  <InfinityIcon className="w-3.5 h-3.5 text-accent" /> Comissão vitalícia
                </span>
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-background/60 border border-primary/20 text-xs text-foreground">
                  <Users className="w-3.5 h-3.5 text-accent" /> 3 cadastros = ativação
                </span>
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-background/60 border border-primary/20 text-xs text-foreground">
                  <ChessQueen className="w-3.5 h-3.5 text-accent" /> Status premium
                </span>
              </div>
            </div>

            {/* CTA */}
            <div className="flex justify-center md:justify-end">
              <Link
                to="/cadastro"
                className="group/btn inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-primary to-accent text-primary-foreground font-semibold text-sm shadow-lg shadow-primary/30 hover:shadow-[0_0_30px_-5px_hsl(var(--primary)/0.6)] hover:scale-[1.02] transition-all whitespace-nowrap"
              >
                Quero ser Embaixador
                <ArrowRight className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Benefits;
